import React, { useEffect, useState, useMemo } from 'react';
import { Form, Typography, Button, Card, Divider, message } from 'antd';
import { SaveOutlined, EnvironmentOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import CommonSelect from '../../../global/select';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedPerfectVenue = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();
    const [venueOptions, setVenueOptions] = useState([]);
    const email = Form.useWatch('email', form);
    const password = Form.useWatch('password', form);

    const DESTINATION_PERFECTVENUE = SOURCES.DESTINATION_PERFECT_VENUE || "perfectvenue";

    // Query for fetching venues
    const { data: venuesData, isFetching: isVenuesLoading, refetch: refetchVenues } = useGenericQuery({
        queryKey: [QUERY_KEYS.destinations.all, 'perfectvenue-venues', email],
        endpoint: "integration/perfectvenue-venues",
        queryParams: {
            destinationAccountEmail: email,
            destinationAccountPassword: password
        },
        enabled: false,
        onSuccess: (data) => {
            if (data && Array.isArray(data)) {
                const formatted = data.map(v => ({
                    label: v.venue?.name || "Unnamed Venue",
                    value: v.venue?.id?.toString()
                }));
                setVenueOptions(formatted);
            }
        }
    });

    const { mutate: savePerfectVenue, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => message.success("Perfect Venue configuration saved successfully")
    });

    useEffect(() => {
        if (venuesData && Array.isArray(venuesData)) {
            const formatted = venuesData.map(v => ({
                label: v.venue?.name || "Unnamed Venue",
                value: v.venue?.id?.toString()
            }));
            setVenueOptions(formatted);
        }
    }, [venuesData]);

    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_PERFECTVENUE);
            if (config) {
                form.setFieldsValue({
                    email: config.destinationAccountEmail,
                    password: config.destinationAccountPassword,
                    sendLeadsLimit: config.leadSentConfig?.sentLimit || 0,
                    venueId: config.extraInfo?.venue_id
                });

                if (config.perfectVenues) {
                    setVenueOptions(config.perfectVenues.map(v => ({
                        label: v.label || v.name,
                        value: v.value?.toString() || v.id?.toString()
                    })));
                }

                if (config.destinationAccountEmail && config.destinationAccountPassword) {
                    refetchVenues();
                }
            }
        }
    }, [destinations, form]);

    const onFinish = (values) => {
        savePerfectVenue({
            destinationAccountEmail: values.email,
            destinationAccountPassword: values.password,
            sendLeadsLimit: values.sendLeadsLimit,
            extraInfo: { venue_id: values.venueId },
            perfectVenues: venueOptions,
            integrationCompany: DESTINATION_PERFECTVENUE,
        });
    };

    const handleFetchVenues = () => {
        const email = form.getFieldValue("email");
        const password = form.getFieldValue("password");
        if (email && password) {
            refetchVenues();
        } else {
            message.warning("Please enter Email and Password first");
        }
    };

    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Email",
            name: "email",
            placeholder: "Enter Perfect Venue Email",
            required: true,
            type: "email",
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Password",
            name: "password",
            placeholder: "Enter Perfect Venue Password",
            required: true,
            type: "password",
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: (
                <div className="space-y-4 mb-4">
                    <Button
                        icon={isVenuesLoading ? null : <EnvironmentOutlined />}
                        onClick={handleFetchVenues}
                        className="h-10 w-full border-dashed hover:border-primary hover:!text-primary"
                    >
                        {isVenuesLoading && <Spinner className={"!border-primary"} />}
                        Get Venue(s)
                    </Button>
                    <Form.Item
                        name="venueId"
                        label="Select Venue"
                        className="mb-0"
                    >
                        <CommonSelect
                            placeholder="Select a Venue"
                            options={venueOptions}
                            loading={isVenuesLoading}
                        />
                    </Form.Item>
                </div>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Initial Lead Send Limit",
            name: "sendLeadsLimit",
            placeholder: "Leads limit",
            type: "number",
            span: 24,
        }
    ], [venueOptions, isVenuesLoading]);

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Connect your Perfect Venue account and sync leads.</Text>
                </div>
            </div>

            <Card className="rounded-2xl border-none shadow-sm">
                <CommonForm
                    form={form}
                    data={formData}
                    onFinish={onFinish}
                    layoutType="vertical"
                />
                <Divider className="mb-4 border-gray-100" />
                <div className="flex justify-start">
                    <Button
                        size="large"
                        icon={isSaving ? null : <SaveOutlined />}
                        onClick={() => form.submit()}
                        className={cn(btnClassName)}
                    >
                        {isSaving && <Spinner className={"!border-white"} />}
                        Save Configuration
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default EnhancedPerfectVenue;
